import { nanoid } from 'nanoid';
import type {
  CustomerAccess,
  FieldError,
  FieldType,
  FieldWidth,
  JsonSchemaType,
  MetadataBuilderState,
  MetadataField,
  MetadataGroup,
} from './types';

/**
 * Convert a JsonSchemaType to the internal MetadataBuilderState
 */
export function jsonSchemaToBuilderState(schema?: JsonSchemaType): MetadataBuilderState {
  if (!schema) {
    return {
      groups: [createDefaultGroup()],
      fields: [],
    };
  }

  const groups: MetadataGroup[] = schema.groups?.length
    ? schema.groups.map((g, index) => ({
        ...g,
        order: g.order ?? index + 1,
      }))
    : [createDefaultGroup()];

  const fields: MetadataField[] = schema.propertyOrder.map((key, index) => {
    const prop = schema.properties[key];
    return {
      id: key,
      key,
      labelDe: prop.description,
      labelEn: prop.description_en || '',
      type: prop.type === 'string' && prop.format === 'date-time' ? 'date' : prop.type,
      required: schema.required.includes(key),
      customerAccess: (prop.customerAccess || 'read') as CustomerAccess,
      groupId: prop.groupId || groups[0]?.id || '',
      width: (prop.width || '100') as FieldWidth,
      order: index + 1,
    };
  });

  return { groups, fields };
}

/**
 * Convert the internal MetadataBuilderState back to JsonSchemaType
 */
export function builderStateToJsonSchema(state: MetadataBuilderState): JsonSchemaType {
  const sortedGroups = [...state.groups].sort((a, b) => a.order - b.order);
  const sortedFields = [...state.fields].sort((a, b) => a.order - b.order);

  const properties: JsonSchemaType['properties'] = {};
  const required: string[] = [];
  const propertyOrder: string[] = [];

  for (const field of sortedFields) {
    const key = field.key.startsWith('temp-')
      ? `${toCamelCase(field.labelDe)}-${nanoid(8)}`
      : field.key;

    properties[key] =
      field.type === 'date'
        ? {
            type: 'string',
            format: 'date-time',
            description: field.labelDe,
            description_en: field.labelEn || undefined,
            customerAccess: field.customerAccess,
            groupId: field.groupId,
            width: field.width,
          }
        : field.type === 'string'
          ? {
              type: field.type,
              description: field.labelDe,
              description_en: field.labelEn || undefined,
              customerAccess: field.customerAccess,
              groupId: field.groupId,
              width: field.width,
              ...(field.required && { minLength: 1 }),
            }
          : {
              type: field.type,
              description: field.labelDe,
              description_en: field.labelEn || undefined,
              customerAccess: field.customerAccess,
              groupId: field.groupId,
              width: field.width,
            };

    // Sections cannot be required
    if (field.required && field.type !== 'boolean' && field.type !== 'section') {
      required.push(key);
    }

    propertyOrder.push(key);
  }

  return {
    type: 'object',
    groups: sortedGroups,
    properties,
    required,
    propertyOrder,
  };
}

/**
 * Create a new default group
 */
export function createDefaultGroup(): MetadataGroup {
  return {
    id: `group-${nanoid(8)}`,
    labelDe: 'Allgemein',
    labelEn: 'General',
    order: 1,
  };
}

/**
 * Create a new group with incremented order
 */
export function createNewGroup(existingGroups: MetadataGroup[]): MetadataGroup {
  const maxOrder = Math.max(0, ...existingGroups.map((g) => g.order));
  return {
    id: `group-${nanoid(8)}`,
    labelDe: 'Neue Gruppe',
    labelEn: 'New Group',
    order: maxOrder + 1,
  };
}

/**
 * Create a new field with default values
 */
export function createNewField(
  groupId: string,
  existingFields: MetadataField[],
  type: FieldType = 'string'
): MetadataField {
  const groupFields = existingFields.filter((f) => f.groupId === groupId);
  const maxOrder = Math.max(0, ...groupFields.map((f) => f.order));

  return {
    id: `temp-${nanoid(8)}`,
    key: `temp-${nanoid(8)}`,
    labelDe: '',
    labelEn: '',
    type,
    required: false,
    customerAccess: 'read',
    groupId,
    width: type === 'section' ? '100' : '50',
    order: maxOrder + 1,
  };
}

/**
 * Convert string to camelCase for field keys
 */
function toCamelCase(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Reorder items in an array (for drag and drop)
 */
export function reorderArray<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const result = Array.from(array);
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

/**
 * Update order values after reordering
 */
export function updateOrderValues<T extends { order: number }>(items: T[]): T[] {
  return items.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
}

/**
 * Parse backend API errors into field errors
 * This handles the specific error codes returned by the metadata validation
 */
interface ParsedBackendError {
  code: string;
  field: string;
  message: string;
  details?: {
    fields?: string[];
    oldType?: string;
    newType?: string;
  };
}

/**
 * Parse backend error response and return field-specific errors.
 * This function extracts validation errors from the Payload CMS error response
 * and maps them to specific fields in the builder.
 *
 * @param error - The error from the API call
 * @param t - Translation function (must accept metadata.fieldMustBeOptional and metadata.fieldTypeChangeNotAllowed)
 */
export function parseMetadataApiErrors(
  error: unknown,
  t: (key: 'metadata.fieldMustBeOptional' | 'metadata.fieldTypeChangeNotAllowed') => string
): FieldError[] {
  const errors: FieldError[] = [];

  const payloadError = error as {
    response?: {
      errors?: Array<{
        name?: string;
        data?: {
          errors?: ParsedBackendError[];
        };
        message?: string;
      }>;
    };
  };

  if (!payloadError.response?.errors) {
    return errors;
  }

  for (const err of payloadError.response.errors) {
    if (err.data?.errors) {
      for (const apiError of err.data.errors) {
        switch (apiError.code) {
          case 'NewRequiredFieldAdditionNotAllowed':
          case 'ExistingFieldMadeRequiredWithMissingValues':
            // These errors contain a list of affected fields
            if (apiError.details?.fields) {
              for (const field of apiError.details.fields) {
                errors.push({
                  fieldKey: field,
                  errorType: 'required',
                  message: t('metadata.fieldMustBeOptional'),
                });
              }
            }
            break;

          case 'FieldTypeChangeNotAllowed':
            // This error is for a single field
            errors.push({
              fieldKey: apiError.field,
              errorType: 'type',
              message: t('metadata.fieldTypeChangeNotAllowed'),
            });
            break;

          default:
            // Generic error - could be shown as toast instead
            break;
        }
      }
    }
  }

  return errors;
}

/**
 * Get a set of group IDs that have fields with errors
 */
export function getGroupsWithErrors(
  fields: MetadataField[],
  fieldErrors: FieldError[]
): Set<string> {
  const groupsWithErrors = new Set<string>();

  for (const error of fieldErrors) {
    const field = fields.find((f) => f.key === error.fieldKey || f.id === error.fieldKey);
    if (field) {
      groupsWithErrors.add(field.groupId);
    }
  }

  return groupsWithErrors;
}
